import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from './../../api/axios';

const BASE_SCORE = { active: 100, ready: 100, configured: 85, attention: 55, permission_required: 40, reconnect_required: 25, inactive: 0, not_connected: 0 };

export function scoreFor(state, detail, activity) {
  let score = BASE_SCORE[state] ?? 50;
  if (detail?.error || detail?.lastError) score -= 15;
  if (detail?.syncHealth?.stale) score -= 20;
  if (detail?.syncHealth?.healthy === false) score -= 15;
  if ((activity?.stats?.failureRate24h || 0) >= 0.5 && (activity?.stats?.failed24h || 0) > 0) score -= 20;
  else if ((activity?.stats?.failed24h || 0) > 0) score -= 8;
  return Math.max(0, Math.min(100, score));
}

export function healthMeta(score) {
  if (score >= 90) return { label: 'Healthy', className: 'text-emerald-700 bg-emerald-50 ring-emerald-200' };
  if (score >= 65) return { label: 'Good', className: 'text-blue-700 bg-blue-50 ring-blue-200' };
  if (score >= 35) return { label: 'Needs attention', className: 'text-amber-800 bg-amber-50 ring-amber-200' };
  return { label: 'Action required', className: 'text-red-700 bg-red-50 ring-red-200' };
}

export default function IntegrationHealthPanel({ item, state, detail, onConnect, onRefresh, connecting, management }) {
  const [activity, setActivity] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionMessage, setActionMessage] = useState('');

  const loadActivity = useCallback(async () => {
    if (!item?.activityScope) return setActivity(null);
    try {
      const { data } = await api.get('/integrations/config/activity', { params: { ...item.activityScope, limit: 5 } });
      setActivity(data || null);
    } catch (_) { setActivity(null); }
  }, [item]);
  useEffect(() => { loadActivity(); }, [loadActivity]);

  const score = useMemo(() => scoreFor(state, detail, activity), [state, detail, activity]);
  const meta = healthMeta(score);
  const crmNeedsSync = item?.activityScope?.kind === 'crm' && ['error', 'stale', 'degraded'].includes(detail?.syncHealth?.status);
  const disconnected = ['inactive', 'not_connected', 'reconnect_required', 'permission_required'].includes(state);
  const policy = item?.providerKey ? management?.providers?.[item.providerKey] : null;
  const connectionManagedByAdmin = policy?.mode === 'organization' && management?.isAdmin !== true;
  const canManageConnection = !connectionManagedByAdmin;

  const runCrmSync = async () => {
    setActionBusy(true); setActionMessage('');
    try {
      await api.post(`/integrations/crm/${item.providerKey}/sync/pull`, { limit: 100 });
      setActionMessage('CRM sync completed.');
      await Promise.all([onRefresh?.(), loadActivity()]);
    } catch (error) {
      setActionMessage(error.response?.data?.message || 'CRM sync could not be completed.');
    } finally { setActionBusy(false); }
  };

  return (
    <section className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      <div className="p-5 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2"><h3 className="text-sm font-semibold text-gray-900">Integration health</h3><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${meta.className}`}>{score}/100 · {meta.label}</span></div>
          <p className="mt-1 text-sm text-gray-500">Based on connection state, permissions, sync freshness and recent operational failures.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {disconnected && item.startEndpoint && canManageConnection && <button type="button" disabled={connecting || actionBusy} onClick={() => onConnect(item)} className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"><i className="fas fa-link mr-2" />{state === 'reconnect_required' || state === 'permission_required' ? 'Reconnect' : 'Connect'}</button>}
          {disconnected && connectionManagedByAdmin && <span className="rounded-lg bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-700 ring-1 ring-indigo-200"><i className="fas fa-building-shield mr-2" />Admin manages connection</span>}
          {crmNeedsSync && <button type="button" disabled={actionBusy} onClick={runCrmSync} className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"><i className={`fas ${actionBusy ? 'fa-spinner fa-spin' : 'fa-rotate'} mr-2`} />Retry sync</button>}
          {!disconnected && !crmNeedsSync && <button type="button" disabled={actionBusy} onClick={async () => { setActionBusy(true); await onRefresh?.(); await loadActivity(); setActionBusy(false); }} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"><i className={`fas ${actionBusy ? 'fa-spinner fa-spin' : 'fa-shield-check'} mr-2`} />Check health</button>}
        </div>
      </div>
      <div className="grid sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-gray-100">
        <div className="p-4"><div className="text-xs text-gray-500">Connection</div><div className="mt-1 text-sm font-semibold text-gray-900">{disconnected ? 'Action needed' : 'Connected'}</div></div>
        <div className="p-4"><div className="text-xs text-gray-500">Failures · 24h</div><div className={`mt-1 text-sm font-semibold ${(activity?.stats?.failed24h || 0) ? 'text-amber-700' : 'text-gray-900'}`}>{activity?.stats?.failed24h || 0}</div></div>
        <div className="p-4"><div className="text-xs text-gray-500">Sync / permission</div><div className="mt-1 text-sm font-semibold text-gray-900">{detail?.syncHealth?.status || (state === 'permission_required' ? 'Permission required' : state === 'reconnect_required' ? 'Reconnect required' : 'OK')}</div></div>
      </div>
      {(detail?.error || detail?.lastError || activity?.stats?.lastError) && <div className="border-t border-gray-100 bg-amber-50/60 px-5 py-3 text-sm text-amber-800"><i className="fas fa-triangle-exclamation mr-2" />{String(detail?.error || detail?.lastError || activity?.stats?.lastError)}</div>}
      {actionMessage && <div className="border-t border-gray-100 px-5 py-3 text-sm text-gray-700">{actionMessage}</div>}
    </section>
  );
}
